/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import type {MessageAttachment} from '@fluxer/schema/src/domains/message/MessageResponseSchemas';

const IMAGE_EXTENSIONS = new Set(['avif', 'bmp', 'gif', 'ico', 'jpg', 'jpeg', 'png', 'svg', 'webp']);
const VIDEO_EXTENSIONS = new Set(['avi', 'm4v', 'mkv', 'mov', 'mp4', 'ogg', 'ogv', 'webm']);
const AUDIO_EXTENSIONS = new Set(['aac', 'flac', 'm4a', 'mp3', 'oga', 'ogg', 'opus', 'wav', 'webm']);

function getExtension(filename?: string | null): string {
	const value = filename?.split('?')[0]?.split('#')[0] ?? '';
	const dotIndex = value.lastIndexOf('.');
	return dotIndex === -1 ? '' : value.slice(dotIndex + 1).toLowerCase();
}

function isImageType(contentType?: string, filename?: string | null): boolean {
	return Boolean(contentType?.startsWith('image/')) || IMAGE_EXTENSIONS.has(getExtension(filename));
}

function isVideoType(contentType?: string, filename?: string | null): boolean {
	return Boolean(contentType?.startsWith('video/')) || VIDEO_EXTENSIONS.has(getExtension(filename));
}

function isAudioType(contentType?: string, filename?: string | null): boolean {
	return Boolean(contentType?.startsWith('audio/')) || AUDIO_EXTENSIONS.has(getExtension(filename));
}

const hasDimensions = (attachment: MessageAttachment): boolean =>
	typeof attachment.width === 'number' && typeof attachment.height === 'number';

export const isImageAttachment = (attachment: MessageAttachment): boolean =>
	isImageType(attachment.content_type, attachment.filename);

export const isVideoAttachment = (attachment: MessageAttachment): boolean =>
	isVideoType(attachment.content_type, attachment.filename);

export const isAudioAttachment = (attachment: MessageAttachment): boolean =>
	isAudioType(attachment.content_type, attachment.filename);

export const isGifAttachment = (attachment: MessageAttachment): boolean =>
	attachment.content_type === 'image/gif' || getExtension(attachment.filename) === 'gif';

export const isMediaAttachment = (attachment: MessageAttachment): boolean =>
	hasDimensions(attachment) &&
	(isImageAttachment(attachment) || isVideoAttachment(attachment) || isAudioAttachment(attachment));

export function splitMediaAndFileAttachments(attachments: ReadonlyArray<MessageAttachment>): {
	mediaAttachments: Array<MessageAttachment>;
	fileAttachments: Array<MessageAttachment>;
} {
	const mediaAttachments: Array<MessageAttachment> = [];
	const fileAttachments: Array<MessageAttachment> = [];

	for (const attachment of attachments) {
		if (isMediaAttachment(attachment)) {
			mediaAttachments.push(attachment);
		} else {
			fileAttachments.push(attachment);
		}
	}

	return {mediaAttachments, fileAttachments};
}
